import { VideoView } from "expo-video";
import { cssInterop } from "nativewind";
import { SectionList } from "react-native";
import Animated from "react-native-reanimated";
import { Carousel } from "react-native-reanimated-carousel";

/**
 * Centralized NativeWind `cssInterop` registrations for third-party / animated
 * components that NativeWind does not register out of the box.
 *
 * Why this exists:
 * NativeWind only auto-registers its `className` -> `style` mapping (and remaps
 * such as `contentContainerClassName` -> `contentContainerStyle`) for the core
 * React Native components. react-native-reanimated's `Animated.*` are distinct
 * components produced by `createAnimatedComponent`, so they are NOT in that map.
 *
 * On native this happens to keep working: reanimated's own source is run through
 * NativeWind's babel transform, so it forwards `className` down to the wrapped
 * core component (which IS registered) and the remap fires there. On web that
 * chain doesn't apply (react-native-web is excluded from the transform and
 * reanimated applies styles via refs), so `contentContainerClassName` is dropped
 * and `className` + an animated `style` fail to merge.
 *
 * Registering the outer components here makes the mapping explicit and identical
 * on every platform — most importantly on web.
 *
 * Call `setupCssInterop()` once, at the top of every app's entry point
 * (`app/_layout.tsx`), before any of these components render.
 */

let registered = false;

export function setupCssInterop(): void {
  if (registered) return;
  registered = true;

  // --- react-native-reanimated animated primitives -------------------------
  // Guarded so a primitive missing from the installed reanimated version is
  // skipped instead of throwing at startup.
  if (Animated?.View) {
    cssInterop(Animated.View, { className: "style" });
  }
  if (Animated?.Text) {
    cssInterop(Animated.Text, { className: "style" });
  }
  if (Animated?.Image) {
    cssInterop(Animated.Image, { className: "style" });
  }
  if (Animated?.ScrollView) {
    cssInterop(Animated.ScrollView, {
      className: "style",
      contentContainerClassName: "contentContainerStyle",
    });
  }
  if (Animated?.FlatList) {
    cssInterop(Animated.FlatList, {
      className: "style",
      contentContainerClassName: "contentContainerStyle",
      ListHeaderComponentClassName: "ListHeaderComponentStyle",
      ListFooterComponentClassName: "ListFooterComponentStyle",
      columnWrapperClassName: "columnWrapperStyle",
    });
  }

  // --- core React Native components NativeWind doesn't register ------------
  // css-interop registers FlatList and VirtualizedList but NOT SectionList, so
  // its className / contentContainerClassName are dropped on web. Mirror the
  // VirtualizedList remaps it's based on.
  cssInterop(SectionList, {
    className: "style",
    contentContainerClassName: "contentContainerStyle",
    ListHeaderComponentClassName: "ListHeaderComponentStyle",
    ListFooterComponentClassName: "ListFooterComponentStyle",
  });

  // --- third-party components registered manually in app entry points -------
  // expo-video
  cssInterop(VideoView, { className: "style" });

  // react-native-reanimated-carousel
  // v5 swapped `style` (now the outer wrapper) and `contentContainerStyle`
  // (now the per-item content) relative to v4's `style`/`containerStyle`.
  // Mirror packages/components/src/ui/carousel/index.tsx's mapping.
  cssInterop(Carousel, {
    className: "contentContainerStyle",
    containerClassName: "style",
  });
}
